import { useState, useEffect, useRef } from 'react';
import { Card, Button, Tooltip, Tag, Typography, message } from 'antd';
import {
	CheckCircleOutlined,
	ToolOutlined,
	DisconnectOutlined,
	CheckCircleFilled,
} from '@ant-design/icons';
import ModalIntegration from '../ModelIntegration';
import { CardItemProps } from '../types';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getLoggedInUser, getSelectedAgent, getSelectedAgentId } from '@/store/account/selector';
import { useQuery } from '@tanstack/react-query';
import {
	checkGoHighLevelConnection,
	getGoHighLevelAuthUrl,
	disconnectGoHighLevel,
	createGoHighLevelOAuthState,
	processGoHighLevelCallback,
} from '../GoHighLevelIntegration';
import { safeJSONParse } from '@/utils/helper';

const { Paragraph, Text } = Typography;

const CardItem = ({ item, getIconColor }: CardItemProps) => {
	const [modalVisible, setModalVisible] = useState(false);
	const [businessTypes, setBusinessTypes] = useState<string[]>([]);
	const [isIntegrating, setIsIntegrating] = useState(false);
	const [ghlAuthUrl, setGhlAuthUrl] = useState('');

	// Reference for callback tracking
	const callbackInProgress = useRef(false);
	const selectedAgent = useAppSelector(getSelectedAgent);
	const selectedAgentId = useAppSelector(getSelectedAgentId);

	// Get logged in user info for OAuth
	const loggedInUser = useAppSelector(getLoggedInUser);
	const username = (selectedAgentId as string) || '';
	const [userDataLoaded, setUserDataLoaded] = useState(false);
	const redirectURL = import.meta.env.VITE_APP_API_BASE_URL + '/api/v1/gohighlevel/auth/callback';

	// Track when user data is loaded
	useEffect(() => {
		if (selectedAgent && selectedAgent.number) {
			setUserDataLoaded(true);
		}
	}, [selectedAgent]);

	// Get reseller check status
	const getResellerEmailCheck = () => {
		if (!userDataLoaded || !loggedInUser) {
			return null;
		}
		return loggedInUser.resellerId ? 'RESELLER' : 'RECEPTIONIST';
	};

	const resellerEmailCheck = getResellerEmailCheck();

	// Query for GoHighLevel connection status
	const {
		data: connectionData,
		isLoading: connectionLoading,
		refetch: refetchConnection,
	} = useQuery({
		queryKey: ['gohighlevel-connection', username, resellerEmailCheck],
		queryFn: () => {
			if (item.name === 'Gohighlevel' && resellerEmailCheck) {
				return checkGoHighLevelConnection({
					resellerEmailCheck,
					agentId: selectedAgentId as string,
				});
			}
			return { isConnected: false };
		},
		enabled: item.name === 'Gohighlevel' && !!username && !!resellerEmailCheck,
	});

	useEffect(() => {
		try {
			if (item.businessType) {
				if (typeof item.businessType === 'string' && item.businessType.startsWith('[')) {
					const parsed = safeJSONParse(item.businessType, []);
					setBusinessTypes(Array.isArray(parsed) ? parsed : []);
				} else {
					setBusinessTypes(
						Array.isArray(item.businessType) ? item.businessType : [item.businessType],
					);
				}
			}
		} catch (error) {
			console.error('Error parsing business types:', error);
			setBusinessTypes([]);
		}
	}, [item]);

	// Determine connection status for UI purposes
	const getConnectionStatus = () => {
		if (connectionLoading) {
			return 'checking';
		}
		if (isIntegrating) {
			return 'connecting';
		}
		if (connectionData?.isConnected) {
			return 'connected';
		}
		return 'disconnected';
	};

	const connectionStatus = getConnectionStatus();

	// Check for OAuth callback in URL (for GoHighLevel)
	useEffect(() => {
		if (item.name !== 'Gohighlevel') {
			return;
		}

		const params = new URLSearchParams(window.location.search);
		const code = params.get('code');
		const integrationSuccess = params.get('integration_success');

		if (code && !callbackInProgress.current) {
			callbackInProgress.current = true;
			setIsIntegrating(true);

			// Process the OAuth callback
			const processCallback = async () => {
				try {
					if (!resellerEmailCheck) {
						return;
					}

					const response = await processGoHighLevelCallback(code, resellerEmailCheck, redirectURL);

					if (response && response.success !== false) {
						message.success('Successfully connected to GoHighLevel!');
						refetchConnection();
						setModalVisible(true);
					} else {
						message.error('Failed to complete GoHighLevel integration.');
					}
				} catch (error) {
					console.error('Error in GoHighLevel callback:', error);
					message.error('An error occurred during connection.');
				} finally {
					callbackInProgress.current = false;
					setIsIntegrating(false);

					// Clean up URL
					if (window.history.replaceState) {
						const newUrl = window.location.href.split('?')[0];
						window.history.replaceState({}, document.title, newUrl);
					}
				}
			};

			if (resellerEmailCheck) {
				processCallback();
			}
		} else if (integrationSuccess === 'gohighlevel') {
			refetchConnection();
			if (window.history.replaceState) {
				const baseUrl = window.location.href.split('?')[0];
				window.history.replaceState({}, document.title, baseUrl);
			}
		}
	}, [item.name, refetchConnection, resellerEmailCheck]);

	// Get GoHighLevel Auth URL when needed
	useEffect(() => {
		// Only get the auth URL when we need it (when GoHighLevel card is clicked)
		if (item.name === 'Gohighlevel' && !connectionData?.isConnected && !ghlAuthUrl) {
			const getAuthUrl = async () => {
				if (!username || !resellerEmailCheck) {
					return;
				}

				try {
					const currentPath = window.location.pathname;
					const searchParams = new URLSearchParams(window.location.search);
					searchParams.set('integration', 'gohighlevel');

					// Create OAuth state parameter
					const state = createGoHighLevelOAuthState(
						username,
						resellerEmailCheck,
						currentPath,
						searchParams,
					);

					// Get the authorization URL
					const authUrl = await getGoHighLevelAuthUrl(
						username,
						resellerEmailCheck,
						redirectURL,
						state,
					);
					setGhlAuthUrl(authUrl);
				} catch (error) {
					console.error('Failed to get authorization URL:', error);
					message.error('Failed to prepare GoHighLevel connection. Please try again.');
				}
			};

			getAuthUrl();
		}
	}, [item.name, connectionData?.isConnected, username, resellerEmailCheck, ghlAuthUrl]);

	// Handle GoHighLevel disconnect
	const handleGoHighLevelDisconnect = async () => {
		try {
			await disconnectGoHighLevel(username, selectedAgentId as string);
			message.success('Successfully disconnected from GoHighLevel');
			refetchConnection();
		} catch {
			message.error('Failed to disconnect from GoHighLevel');
		}
	};

	// Get icon for business type
	const getBusinessTypeIcon = (type: string) => {
		switch (type.toLowerCase()) {
			case 'hvac':
				return <ToolOutlined />;
			case 'construction':
				return <ToolOutlined />;
			default:
				return <CheckCircleOutlined />;
		}
	};

	const handleCloseModal = () => {
		setModalVisible(false);
		if (isGoHighLevel) {
			refetchConnection();
		}
	};

	// Determine if this is the GoHighLevel card
	const isGoHighLevel = item.name === 'Gohighlevel';

	return (
		<div className="card-overlay-container">
			{/* Base Card */}
			<Card
				key={item.name}
				className={`rounded-lg border-2 hover:border-primary/50 shadow-sm hover:shadow-md transition-all h-full ${
					isGoHighLevel && connectionData?.isConnected ? 'border-green-500' : ''
				}`}
				bodyStyle={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column' }}
			>
				<div className="flex flex-col h-full">
					<div className="flex items-start gap-3 mb-2">
						<div
							className={`h-12 w-12 rounded-lg ${getIconColor(item.name)} text-white flex items-center justify-center font-bold text-lg shadow-sm`}
						>
							{item.name.slice(0, 2).toUpperCase()}
						</div>
						<div className="flex-1 min-w-0">
							<Tooltip title={item.name} placement="top">
								<h3 className="font-semibold text-lg text-gray-800 mb-0 leading-tight truncate">
									{item.name}
								</h3>
							</Tooltip>

							{isGoHighLevel && connectionStatus === 'connected' && (
								<div className="flex items-center text-green-600 mt-1">
									<CheckCircleFilled className="mr-1" />
									<Text className="text-xs text-green-600">Connected</Text>
								</div>
							)}
						</div>
					</div>

					{/* Business Types */}
					{businessTypes.length > 0 && (
						<div className="mb-3 ml-1">
							<div className="flex flex-wrap gap-1 mt-1">
								{businessTypes.map((type, index) => (
									<Tag
										key={index}
										color="green"
										className="text-xs flex items-center py-0.5 px-2"
										style={{ margin: 0 }}
									>
										{getBusinessTypeIcon(type)}
										<span className="ml-1">{type}</span>
									</Tag>
								))}
							</div>
						</div>
					)}

					{/* Short description */}
					<Paragraph ellipsis={{ rows: 4 }} className="text-sm text-gray-600 mb-3">
						{item.description || 'Integrate your services with this powerful tool.'}
					</Paragraph>

					<div className="mt-auto">
						{isGoHighLevel && connectionStatus === 'connected' ? (
							<>
								<Button
									danger
									className="w-full"
									icon={<DisconnectOutlined />}
									onClick={(e) => {
										e.stopPropagation();
										handleGoHighLevelDisconnect();
									}}
								>
									Disconnect
								</Button>
								<Button
									className="w-full mt-2 border-green-600 text-green-600 hover:border-green-600"
									onClick={(e) => {
										e.stopPropagation();
										setModalVisible(true);
									}}
								>
									Check connection
								</Button>
							</>
						) : (
							<Button
								type="primary"
								onClick={(e) => {
									e.stopPropagation();
									setModalVisible(true);
								}}
								className="w-full py-4 text-base font-medium integrate-btn"
							>
								{' '}
								Integrate Now
							</Button>
						)}
					</div>
				</div>
			</Card>

			{modalVisible && (
				<ModalIntegration
					open={modalVisible}
					selectedIntegration={item.name}
					features={item.features}
					description={item.description}
					integrationType={isGoHighLevel ? 'oauth' : item.type || ''}
					instructionLink={item.instructionLink}
					onClose={handleCloseModal}
					apiList={item.api}
					authUrl={isGoHighLevel ? ghlAuthUrl : undefined}
					resellerEmailCheck={resellerEmailCheck}
					username={username}
					isGoHighLevel={isGoHighLevel}
				/>
			)}
		</div>
	);
};

export default CardItem;
